import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";
import {Device, DeviceFlat} from "./model/device";
import {DeviceHistoryEntry} from "./model/deviceHistory";
import {Firmware, FirmwareField} from "./model/firmware";
import {OrganizationStage, Stage, StageField} from "./model/stage";
import {User} from "./model/user";
import {Order, OrderField, OrderFlat, OrderStatus} from "./model/order";
import {
    ActiveDriverCardsResponse,
    ApiTag,
    CancelRequest,
    CompanyCardByIdRequest,
    CompanyCardCreateRequest,
    CompanyCardListRequest,
    CreateResponse,
    DeviceDriverCardDownloadRequest,
    DeviceFirmwareUpdateRequest,
    DeviceRestartFirmwareUpdateRequest,
    DeviceMassStorageDownloadRequest,
    DeviceRdlAuthenticateRequest,
    DeviceRdlAuthenticationStatusResponse,
    DevicesCommandRequest,
    DeviceUpdateStagesRequest,
    DkvCustomerNameResolveRequest,
    DkvCustomerNameResolveResponse,
    DkvUpdateCustomerNameRequest,
    EetsDeviceByIdRequest,
    IdListRequest,
    ListRequest,
    ListRequestParams,
    ListResponse,
    LoginRequest,
    OrderUpdateAdminUserRequest,
    OrderUpdateCommentRequest,
    OrderUpdateRecipientRequest,
    OrganizationAddStageRequest,
    OrganizationCreateFromOrderRequest,
    OrganizationCreateFromOrderResponse,
    OrganizationCreateRequest,
    OrganizationDevicesRequest,
    OrganizationDKVCustomerNumberCreateRequest,
    OrganizationIdListRequest,
    OrganizationUpdateRequest,
    RoadsoftConfigRequest,
    SessionResponse,
    StandardResponse,
    UpdatePasswordRequest,
    UpdateStageDefaultFirmwareRequest,
    UserCreateRequest,
    UserUpdateRequest,
} from "./types";
import {Shipment, ShipmentField, ShipmentFlat, ShipmentStatus} from "./model/shipment";
import {DkvCustomerNumber, Organization} from "./model/organization";
import {CompanyCardDetail, CompanyCardServer} from "./model/organization/companyCard";
import {EetsDevice, EetsDeviceHistoryEntry} from "./model/eetsDevice";
import {AdminUser} from "./model/adminUser/adminUser";
import _ from "lodash";
import {Termination, TerminationCommunication, TerminationField, TerminationItem} from "./model/termination";
import {Asset} from "./model/asset";
import {DeviceJob, DeviceJobField} from "./model/deviceJob";
import {RoadsoftConfig} from "./model/roadsoft";
import {configuration} from "../../config/config";

export * from "./types";

const getQueryFromParams = (params: ListRequestParams = {}, defaultParams: ListRequestParams = {}) => {
    const query = new URLSearchParams();
    const queryParams = {..._.omitBy(defaultParams, _.isUndefined), ..._.omitBy(params, _.isUndefined)};
    for (const key in queryParams) {
        query.set(key, `${queryParams[key]}`);
    }

    return query;
};

const TAG_LIST = "LIST";

export const api = createApi({
    baseQuery: fetchBaseQuery({
        baseUrl: configuration.apiUrl,
    }),
    tagTypes: [...Object.keys(ApiTag)],
    refetchOnFocus: true,
    refetchOnReconnect: true,
    endpoints: (build) => ({
        login: build.mutation<void, LoginRequest>({
            query: (request) => ({
                url: `/login`,
                method: "POST",
                credentials: "include",
                body: request,
            }),
            invalidatesTags: [ApiTag.SESSION, ApiTag.USER],
        }),
        logout: build.mutation<StandardResponse, void>({
            query: () => ({
                url: "/logout",
                method: "POST",
                credentials: "include",
            }),
            invalidatesTags: [ApiTag.SESSION, ApiTag.USER],
        }),
        session: build.query<SessionResponse, void>({
            query: () => ({
                url: "/session",
                method: "GET",
                credentials: "include",
            }),
            transformResponse: (response: {payload: SessionResponse}) => response.payload,
            providesTags: [ApiTag.SESSION],
        }),
        passwordUpdate: build.mutation<void, UpdatePasswordRequest>({
            query: (request) => ({
                url: `/password`,
                method: "PUT",
                credentials: "include",
                body: request,
            }),
        }),
        assetList: build.query<ListResponse<Asset>, ListRequest>({
            query: (request) => {
                const query = getQueryFromParams(request.params);
                return {
                    url: `/assets?${query.toString()}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.ASSET],
        }),
        deviceList: build.query<ListResponse<DeviceFlat>, ListRequest>({
            query: (request) => {
                const query = getQueryFromParams(request.params);
                return {
                    url: `/devices?${query.toString()}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.DEVICE],
        }),
        deviceGet: build.query<Device, number>({
            query: (id) => {
                return {
                    url: `/devices/${id}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            transformResponse: (response: {payload: Device}) => response.payload,
            providesTags: (_result, _error, arg) => [{type: ApiTag.DEVICE, id: arg}],
        }),
        deviceCreate: build.mutation<CreateResponse, Partial<DeviceFlat>>({
            query: (request) => ({
                url: `/devices`,
                method: "POST",
                credentials: "include",
                body: request,
            }),
            transformResponse: (response: {payload: DeviceFlat}) => response.payload,
            invalidatesTags: [ApiTag.DEVICE],
        }),
        deviceHistoryList: build.query<ListResponse<DeviceHistoryEntry>, IdListRequest>({
            query: (request) => {
                const query = getQueryFromParams(request.params);
                return {
                    url: `/devices/${request.id}/history?${query.toString()}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.DEVICE_HISTORY],
        }),
        deviceFirmwareUpdate: build.mutation<void, DeviceFirmwareUpdateRequest>({
            query: (request) => ({
                url: `/devices/${request.deviceId}/firmware`,
                method: "POST",
                credentials: "include",
                body: {firmwareId: request.firmwareId, scheduledAt: request.scheduledAt},
            }),
            invalidatesTags: (_result, _error, arg) => [
                {type: ApiTag.DEVICE, id: arg.deviceId},
                {type: ApiTag.DEVICE, id: TAG_LIST},
            ],
        }),
        deviceRestartFirmwareUpdate: build.mutation<void, DeviceRestartFirmwareUpdateRequest>({
            query: ({deviceIds}) => {
                return {
                    url: `/devices/restart-firmware-update`,
                    method: "POST",
                    credentials: "include",
                    body: {deviceIds},
                };
            },
            invalidatesTags: () => [{type: ApiTag.DEVICE}],
        }),
        // <ListResponse<OrderListEntry>, ListRequestParams>
        eetsDeviceHistoryList: build.query<ListResponse<EetsDeviceHistoryEntry>, OrganizationIdListRequest>({
            query: (request) => {
                const query = getQueryFromParams(request.params);
                return {
                    url: `/organizations/${request.organizationId}/eetsDevices/${
                        request.id
                    }/history?${query.toString()}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.EETS_DEVICE_HISTORY],
        }),
        eetsDeviceByIdGet: build.query<EetsDevice, EetsDeviceByIdRequest>({
            query: (request) => {
                return {
                    url: `/organizations/${request.organizationId}/eetsDevices/${request.id}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            transformResponse: (response: {payload: EetsDevice}) => response.payload,
            providesTags: (_result, _error, arg) => [{type: ApiTag.EETS_DEVICE, id: arg.id}],
        }),
        deviceStagesUpdate: build.mutation<void, DeviceUpdateStagesRequest>({
            query: (request) => ({
                url: `/devices/${request.deviceId}/stages`,
                method: "PUT",
                credentials: "include",
                body: {
                    primaryStage: request.primaryStage,
                    stages: request.stages,
                },
            }),
            invalidatesTags: (_result, _error, arg) => [{type: ApiTag.DEVICE, id: arg.deviceId}],
        }),
        devicesCommand: build.mutation<void, DevicesCommandRequest>({
            query: (request) => ({
                url: `/devices/command`,
                method: "POST",
                credentials: "include",
                body: {
                    deviceIds: request.deviceIds,
                    command: request.command,
                },
            }),
            invalidatesTags: [ApiTag.SMS_COMMANDS, ApiTag.DEVICE],
        }),
        deviceRdlAuthenticate: build.mutation<DeviceRdlAuthenticateRequest, number>({
            query: (deviceId: number) => ({
                url: `devices/${deviceId}/rdl-authenticate`,
                method: "POST",
                credentials: "include",
            }),
            invalidatesTags: [ApiTag.DEVICE],
        }),
        deviceRdlAuthenticationStatus: build.query<DeviceRdlAuthenticationStatusResponse, number>({
            query: (deviceId: number) => ({
                url: `devices/${deviceId}/rdl-authentication-status`,
                method: "GET",
                credentials: "include",
            }),
            transformResponse: (response: {payload: DeviceRdlAuthenticationStatusResponse}) => response.payload,
            providesTags: [ApiTag.DEVICE_RDL_STATUS],
        }),
        deviceMassStorageDownload: build.mutation<void, DeviceMassStorageDownloadRequest>({
            query: (request) => ({
                url: `devices/${request.deviceId}/jobs`,
                method: "POST",
                credentials: "include",
                body: {
                    type: request.type,
                    stage: request.stage,
                    periodStart: request.periodStart,
                    periodEnd: request.periodEnd,
                },
            }),
            invalidatesTags: [ApiTag.DEVICE],
        }),
        deviceDriverCardDownload: build.mutation<void, DeviceDriverCardDownloadRequest>({
            query: (request) => ({
                url: `devices/${request.deviceId}/jobs`,
                method: "POST",
                credentials: "include",
                body: {
                    type: request.type,
                    stage: request.stage,
                    driverCardNumber: request.driverCardNumber,
                    driverCardSlot: request.driverCardSlot,
                },
            }),
            invalidatesTags: [ApiTag.DEVICE],
        }),
        hardwareTypeList: build.query<ListResponse<string>, ListRequest>({
            query: (request) => {
                const query = getQueryFromParams(request.params);
                return {
                    url: `/hardware-types?${query.toString()}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.HARDWARE_TYPE],
        }),
        orderList: build.query<ListResponse<OrderFlat>, ListRequest>({
            query: (request) => {
                const query = getQueryFromParams(request.params, {sort: `-${OrderField.CREATED_AT}`});
                return {
                    url: `/orders?${query.toString()}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.ORDER],
        }),
        orderGet: build.query<Order, number>({
            query: (id) => {
                return {
                    url: `/orders/${id}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            transformResponse: (response: {payload: Order}) => response.payload,
            providesTags: (_result, _error, id) => [{type: ApiTag.ORDER, id}],
        }),
        orderStatusGet: build.query<OrderStatus[], void>({
            query: () => {
                return {
                    url: `/order-statuses`,
                    method: "GET",
                    credentials: "include",
                };
            },
            transformResponse: (response: {payload: OrderStatus[]}) => response.payload,
            providesTags: [ApiTag.ORDER_STATUS],
        }),
        orderUpdateRecipient: build.mutation<void, OrderUpdateRecipientRequest>({
            query: (request) => {
                return {
                    url: `/orders/${request.id}/recipient`,
                    method: "PATCH",
                    credentials: "include",
                    body: {
                        companyName: request.shippingRecipient.companyName,
                        contact: request.shippingRecipient.contact,
                        address: request.shippingRecipient.address,
                    },
                };
            },
            invalidatesTags: [ApiTag.ORDER],
        }),
        orderUpdateAdminUser: build.mutation<void, OrderUpdateAdminUserRequest>({
            query: (request) => {
                return {
                    url: `/orders/${request.id}/admin-user`,
                    method: "PATCH",
                    credentials: "include",
                    body: {
                        email: request.adminUser.email,
                        firstName: request.adminUser.firstName,
                        lastName: request.adminUser.lastName,
                        phone: request.adminUser.phone,
                        language: request.adminUser.language,
                        timeZone: request.adminUser.timeZone,
                    },
                };
            },
            invalidatesTags: [ApiTag.ORDER],
        }),
        orderUpdateComment: build.mutation<void, OrderUpdateCommentRequest>({
            query: (request) => {
                return {
                    url: `/orders/${request.id}/comment`,
                    method: "PUT",
                    credentials: "include",
                    body: {
                        comment: request.comment,
                    },
                };
            },
            invalidatesTags: [ApiTag.ORDER],
        }),
        orderCancel: build.mutation<void, CancelRequest>({
            query: (request) => ({
                url: `/orders/${request.id}/cancel`,
                method: "POST",
                credentials: "include",
                body: request,
            }),
            invalidatesTags: [ApiTag.ORDER],
        }),
        terminationList: build.query<ListResponse<Termination>, ListRequest>({
            query: (request) => {
                const query = getQueryFromParams(request.params, {sort: `-${TerminationField.CREATED_AT}`});
                return {
                    url: `/terminations?${query.toString()}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.TERMINATION],
        }),
        terminationGet: build.query<Termination, number>({
            query: (id) => {
                return {
                    url: `/terminations/${id}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            transformResponse: (response: {payload: Termination}) => response.payload,
            providesTags: (_result, _error, id) => [{type: ApiTag.TERMINATION, id}],
        }),
        terminationUpdate: build.mutation<
            Termination,
            {id: Termination[TerminationField.ID]; termination: Partial<Termination>}
        >({
            query: ({id, termination}) => {
                return {
                    url: `/terminations/${id}`,
                    method: "PATCH",
                    credentials: "include",
                    body: termination,
                };
            },
            transformResponse: (response: {payload: Termination}) => response.payload,
            invalidatesTags: [ApiTag.TERMINATION],
        }),
        terminationComplete: build.mutation<void, {id: number}>({
            query: ({id}) => {
                return {
                    url: `/terminations/${id}/complete`,
                    method: "POST",
                    credentials: "include",
                };
            },
            invalidatesTags: [ApiTag.TERMINATION],
        }),
        terminationItemList: build.query<ListResponse<TerminationItem>, IdListRequest>({
            query: (request) => {
                const query = getQueryFromParams(request.params, {sort: `-${TerminationField.CREATED_AT}`});
                return {
                    url: `/terminations/${request.id}/items?${query.toString()}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.TERMINATION_ITEM],
        }),
        terminationItemUpdate: build.mutation<
            TerminationItem,
            {terminationId: number; id: number; item: Partial<TerminationItem>}
        >({
            query: ({terminationId, id, item}) => {
                return {
                    url: `/terminations/${terminationId}/items/${id}`,
                    method: "PATCH",
                    credentials: "include",
                    body: item,
                };
            },
            transformResponse: (response: {payload: TerminationItem}) => response.payload,
            invalidatesTags: [ApiTag.TERMINATION_ITEM, ApiTag.TERMINATION],
        }),
        terminationCommunicationList: build.query<ListResponse<TerminationCommunication>, IdListRequest>({
            query: (request) => {
                const query = getQueryFromParams(request.params, {sort: `-${TerminationField.CREATED_AT}`});
                return {
                    url: `/terminations/${request.id}/communications?${query.toString()}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.TERMINATION_COMMUNICATION],
        }),
        shipmentList: build.query<ListResponse<ShipmentFlat>, ListRequest>({
            query: (request) => {
                const query = getQueryFromParams(request.params, {sort: `-${ShipmentField.CREATED_AT}`});
                return {
                    url: `/shipments?${query.toString()}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.SHIPMENT],
        }),
        shipmentStatusGet: build.query<ShipmentStatus[], void>({
            query: () => {
                return {
                    url: `/shipment-statuses`,
                    method: "GET",
                    credentials: "include",
                };
            },
            transformResponse: (response: {payload: ShipmentStatus[]}) => response.payload,
            providesTags: [ApiTag.SHIPMENT_STATUS],
        }),
        shipmentGet: build.query<Shipment, number>({
            query: (id) => {
                return {
                    url: `/shipments/${id}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            transformResponse: (response: {payload: Shipment}) => response.payload,
            providesTags: [ApiTag.SHIPMENT],
        }),
        shipmentCancel: build.mutation<void, CancelRequest>({
            query: (request) => ({
                url: `/shipments/${request.id}/cancel`,
                method: "POST",
                credentials: "include",
                body: request,
            }),
            invalidatesTags: [ApiTag.SHIPMENT],
        }),
        orderShipmentCreate: build.mutation<CreateResponse, number>({
            query: (orderId: number) => ({
                url: `/orders/${orderId}/shipment`,
                method: "POST",
                credentials: "include",
            }),
            transformResponse: (response: {payload: Shipment}) => response.payload,
            invalidatesTags: [ApiTag.SHIPMENT, ApiTag.ORDER],
        }),
        defaultFirmwareList: build.query<ListResponse<Firmware>, ListRequest>({
            query: (request) => {
                const query = getQueryFromParams(request.params);
                return {
                    url: `/default-firmwares?${query.toString()}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.DEFAULT_FIRMWARE],
        }),
        defaultFirmwareSet: build.mutation<void, Partial<Firmware>>({
            query: (firmware) => {
                return {
                    url: `/default-firmwares/`,
                    method: "POST",
                    credentials: "include",
                    body: {firmwareId: firmware[FirmwareField.ID]},
                };
            },
            invalidatesTags: [ApiTag.DEFAULT_FIRMWARE],
        }),
        firmwareStateList: build.query<ListResponse<string>, ListRequest>({
            query: (request) => {
                const query = getQueryFromParams(request.params);
                return {
                    url: `/firmware-states?${query.toString()}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.FIRMWARE_STATE],
        }),
        firmwareTargetList: build.query<ListResponse<string>, ListRequest>({
            query: (request) => {
                const query = getQueryFromParams(request.params);
                return {
                    url: `/firmware-targets?${query.toString()}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.FIRMWARE_TARGET],
        }),
        firmwareList: build.query<ListResponse<Firmware>, ListRequest>({
            query: (request) => {
                const query = getQueryFromParams(request.params);
                return {
                    url: `/firmwares?${query.toString()}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.FIRMWARE],
        }),
        firmwareGet: build.query<Firmware, number>({
            query: (id) => {
                return {
                    url: `/firmwares/${id}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            transformResponse: (response: {payload: Firmware}) => response.payload,
            providesTags: (_result, _error, arg) => [{type: ApiTag.FIRMWARE, id: arg}],
        }),
        firmwareCreate: build.mutation<CreateResponse, Partial<Firmware>>({
            query: (firmware) => {
                return {
                    url: `/firmwares/`,
                    method: "POST",
                    credentials: "include",
                    body: firmware,
                };
            },
            transformResponse: (response: {payload: CreateResponse}) => response.payload,
            invalidatesTags: [ApiTag.FIRMWARE],
        }),
        firmwareUpdate: build.mutation<Firmware, Partial<Firmware>>({
            query: (firmware) => {
                return {
                    url: `/firmwares/${firmware[FirmwareField.ID]}`,
                    method: "PATCH",
                    credentials: "include",
                    body: firmware,
                };
            },
            transformResponse: (response: {payload: Firmware}) => response.payload,
            invalidatesTags: (_result, _error, arg) => [{type: ApiTag.FIRMWARE, id: arg[FirmwareField.ID]}],
        }),
        firmwareUpload: build.mutation<void, {id: number; formData: FormData}>({
            query: ({id, formData}) => {
                return {
                    url: `/firmwares/${id}/file`,
                    method: "PUT",
                    credentials: "include",
                    body: formData,
                };
            },
            invalidatesTags: (_result, _error, arg) => [{type: ApiTag.FIRMWARE, id: arg.id}],
        }),
        firmwareDelete: build.mutation<void, number>({
            query: (id) => ({
                url: `/firmwares/${id}`,
                method: "DELETE",
                credentials: "include",
            }),
            invalidatesTags: [ApiTag.FIRMWARE],
        }),
        stageList: build.query<ListResponse<Stage>, ListRequest>({
            query: (request) => {
                const query = getQueryFromParams(request.params);
                return {
                    url: `/stages?${query.toString()}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.STAGE],
        }),
        stageUpdate: build.mutation<void, UpdateStageDefaultFirmwareRequest>({
            query: (request) => ({
                url: `/stages/${request[StageField.IDENTIFIER]}`,
                method: "PUT",
                credentials: "include",
                body: request,
            }),
            invalidatesTags: [{type: ApiTag.STAGE}],
        }),
        userList: build.query<ListResponse<User>, ListRequest>({
            query: (request) => {
                const query = getQueryFromParams(request.params);
                return {
                    url: `/users?${query.toString()}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.USER],
        }),
        userGet: build.query<User, number>({
            query: (id) => {
                return {
                    url: `/users/${id}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            transformResponse: (response: {payload: User}) => response.payload,
            providesTags: [ApiTag.USER],
        }),
        userUpdate: build.mutation<void, UserUpdateRequest>({
            query: (request) => ({
                url: `/users/${request.id}`,
                method: "PUT",
                credentials: "include",
                body: request,
            }),
            invalidatesTags: (_result, _error, arg) => [{type: ApiTag.USER, id: arg.id}, ApiTag.USER],
        }),
        userCreate: build.mutation<CreateResponse, UserCreateRequest>({
            query: (request) => ({
                url: `/users`,
                method: "POST",
                credentials: "include",
                body: request,
            }),
            transformResponse: (response: {payload: User}) => response.payload,
            invalidatesTags: [ApiTag.USER],
        }),
        userDelete: build.mutation<void, number>({
            query: (id) => ({
                url: `/users/${id}`,
                method: "DELETE",
                credentials: "include",
            }),
            invalidatesTags: [ApiTag.USER],
        }),
        organizationList: build.query<ListResponse<Organization>, ListRequest>({
            query: (request) => {
                const query = getQueryFromParams(request.params);

                return {
                    url: `/organizations?${query.toString()}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.ORGANIZATION],
        }),
        organizationGet: build.query<Organization, number>({
            query: (id) => {
                return {
                    url: `/organizations/${id}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            transformResponse: (response: {payload: Organization}) => response.payload,
            providesTags: (_result, _error, id) => [{type: ApiTag.ORGANIZATION, id}, ApiTag.ORGANIZATION],
        }),
        organizationUpdate: build.mutation<void, OrganizationUpdateRequest>({
            query: (request) => {
                const {id, ...body} = request;
                return {
                    url: `/organizations/${id}`,
                    method: "PUT",
                    credentials: "include",
                    body: body,
                };
            },
            invalidatesTags: (_result, _error, arg) => [{type: ApiTag.ORGANIZATION, id: arg.id}],
        }),
        organizationCreate: build.mutation<CreateResponse, OrganizationCreateRequest>({
            query: (request) => ({
                url: `/organizations`,
                method: "POST",
                credentials: "include",
                body: request,
            }),
            transformResponse: (response: {payload: Organization}) => response.payload,
            invalidatesTags: [ApiTag.ORGANIZATION],
        }),
        organizationCreateFromOrder: build.mutation<
            OrganizationCreateFromOrderResponse,
            OrganizationCreateFromOrderRequest
        >({
            query: (request) => ({
                url: `/orders/${request.orderId}/organization`,
                method: "POST",
                credentials: "include",
                body: {
                    stageIdentifier: request.stageIdentifier,
                },
            }),
            transformResponse: (response: {payload: Organization}) => response.payload,
            invalidatesTags: [ApiTag.ORGANIZATION, ApiTag.ORDER],
        }),
        organizationStageList: build.query<ListResponse<OrganizationStage>, IdListRequest>({
            query: (request) => {
                const query = getQueryFromParams(request.params);
                return {
                    url: `/organizations/${request.id}/stages?${query.toString()}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.ORGANIZATION, ApiTag.STAGE],
        }),
        organizationAdminUserList: build.query<ListResponse<AdminUser>, IdListRequest>({
            query: (request) => {
                const query = getQueryFromParams(request.params);
                return {
                    url: `/organizations/${request.id}/adminUsers?${query.toString()}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.ADMIN_USER],
        }),
        organizationDKVCustomerNumberList: build.query<ListResponse<DkvCustomerNumber>, IdListRequest>({
            query: (request) => {
                const query = getQueryFromParams(request.params);
                return {
                    url: `/organizations/${request.id}/dkvCustomerNumbers?${query.toString()}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.ORGANIZATION_DKV_CUSTOMER_NUMBERS],
        }),
        organizationEetsDeviceList: build.query<ListResponse<EetsDevice>, IdListRequest>({
            query: (request) => {
                const query = getQueryFromParams(request.params);
                return {
                    url: `/organizations/${request.id}/eetsDevices?${query.toString()}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.EETS_DEVICE],
        }),
        organizationDeviceJobList: build.query<ListResponse<DeviceJob>, IdListRequest>({
            query: (request) => {
                const query = getQueryFromParams(request.params, {sort: `-${DeviceJobField.REQUESTED_AT}`});
                return {
                    url: `/organizations/${request.id}/jobs?${query.toString()}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.ORGANIZATION_DEVICE_JOB],
        }),
        organizationDKVCustomerNumberCreate: build.mutation<
            DkvCustomerNumber,
            OrganizationDKVCustomerNumberCreateRequest
        >({
            query: (request) => {
                const {organizationId, ...body} = request;
                return {
                    url: `/organizations/${organizationId}/dkvCustomerNumbers`,
                    method: "POST",
                    credentials: "include",
                    body: body,
                };
            },
            invalidatesTags: [ApiTag.ORGANIZATION_DKV_CUSTOMER_NUMBERS, ApiTag.ORGANIZATION],
        }),
        organizationAddStage: build.mutation<void, OrganizationAddStageRequest>({
            query: (request) => {
                return {
                    url: `/organizations/${request.organizationId}/stages`,
                    method: "POST",
                    credentials: "include",
                    body: {
                        stage: request.stage,
                        adminEmail: request.adminEmail,
                    },
                };
            },
            invalidatesTags: [ApiTag.ORGANIZATION],
        }),
        organizationDeviceAssign: build.mutation<void, OrganizationDevicesRequest>({
            query: (request) => {
                return {
                    url: `/organizations/${request.organizationId}/devices`,
                    method: "PATCH",
                    credentials: "include",
                    body: {
                        deviceIds: request.deviceIds,
                        comment: request.comment,
                        action: "assign",
                    },
                };
            },
            invalidatesTags: [ApiTag.DEVICE],
        }),
        organizationDeviceRemove: build.mutation<void, OrganizationDevicesRequest>({
            query: (request) => {
                return {
                    url: `/organizations/${request.organizationId}/devices`,
                    method: "PATCH",
                    credentials: "include",
                    body: {
                        deviceIds: request.deviceIds,
                        action: "remove",
                    },
                };
            },
            invalidatesTags: [ApiTag.DEVICE],
        }),
        dkvCustomerNameUpdate: build.mutation<void, DkvUpdateCustomerNameRequest>({
            query: (request) => {
                const {dkvCustomerNumber, ...body} = request;
                return {
                    url: `/dkv-customers/${dkvCustomerNumber}`,
                    method: "PUT",
                    credentials: "include",
                    body: body,
                };
            },
            invalidatesTags: (_result, _error, arg) => [
                {type: ApiTag.ORGANIZATION_DKV_CUSTOMER_NUMBERS, dkvCustomerNumber: arg.dkvCustomerNumber},
            ],
        }),
        dkvCustomerNameMismatchUpdate: build.mutation<DkvCustomerNameResolveResponse, DkvCustomerNameResolveRequest>({
            query: (request) => ({
                url: `/orders/${request.id}/customer/name/resolve`,
                method: "POST",
                credentials: "include",
                body: request,
            }),
            invalidatesTags: [ApiTag.ORDER],
        }),
        companyCardByIdGet: build.query<CompanyCardDetail, CompanyCardByIdRequest>({
            query: (request) => {
                return {
                    url: `/organizations/${request.organizationId}/companyCards/${request.id}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            transformResponse: (response: {payload: CompanyCardDetail}) => response.payload,
            providesTags: (_result, _error, arg) => [{type: ApiTag.COMPANY_CARD, id: arg.id}],
        }),
        companyCardDelete: build.mutation<void, CompanyCardByIdRequest>({
            query: (request) => ({
                url: `/organizations/${request.organizationId}/companyCards/${request.id}`,
                method: "DELETE",
                credentials: "include",
            }),
            async onQueryStarted({organizationId, id}, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;

                    dispatch(
                        api.util.updateQueryData(
                            "companyCardByIdGet",
                            {organizationId, id},
                            (draft: CompanyCardDetail) => {
                                Object.assign(draft, {
                                    id: undefined,
                                    cardId: undefined,
                                    cardReaderIP: undefined,
                                    cardReaderSlot: undefined,
                                    comment: undefined,
                                    createdAt: undefined,
                                    division: undefined,
                                    organizationId: undefined,
                                    organization: undefined,
                                    updatedAt: undefined,
                                    validUntil: undefined,
                                });
                            }
                        )
                    );
                } catch {
                    //
                }
            },
            invalidatesTags: (_result, _error, arg) => [
                ApiTag.ORGANIZATION,
                {type: ApiTag.ORGANIZATION, id: arg.organizationId},
            ],
        }),
        companyCardList: build.query<ListResponse<CompanyCardDetail>, CompanyCardListRequest>({
            query: (request) => {
                const {organizationId} = request;
                return {
                    url: `/organizations/${organizationId}/companyCards`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.COMPANY_CARD],
        }),
        companyCardUpdate: build.mutation<CreateResponse, CompanyCardCreateRequest>({
            query: (request) => ({
                url: `/organizations/${request.organizationId}/companyCards/${request.id}`,
                method: "PUT",
                credentials: "include",
                body: request,
            }),
            transformResponse: (response: {payload: CreateResponse}) => response.payload,
            invalidatesTags: (_result, _error, arg) => [
                {type: ApiTag.COMPANY_CARD, id: arg.id},
                ApiTag.ORGANIZATION,
                // {type: tagTypes.ORGANIZATION, id: arg.organizationId},
            ],
        }),
        companyCardCreate: build.mutation<CreateResponse, CompanyCardCreateRequest>({
            query: (request) => ({
                url: `/organizations/${request.organizationId}/companyCards`,
                method: "POST",
                credentials: "include",
                body: request,
            }),
            transformResponse: (response: {payload: CreateResponse}) => response.payload,
            invalidatesTags: (_result, _error, arg) => [
                {type: ApiTag.COMPANY_CARD, id: arg.id},
                ApiTag.ORGANIZATION,
                // {type: tagTypes.ORGANIZATION, id: arg.organizationId},
            ],
        }),
        companyCardServer: build.query<ListResponse<CompanyCardServer>, ListRequest>({
            query: () => {
                return {
                    url: `/company-card-servers`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.CARD_SERVER],
        }),
        deviceDriverCards: build.query<ActiveDriverCardsResponse[], {deviceId: number}>({
            query: (request) => {
                return {
                    url: `/devices/${request.deviceId}/driver-cards`,
                    method: "GET",
                    credentials: "include",
                };
            },
            transformResponse: (response: {payload: ActiveDriverCardsResponse[]}) => response.payload,
            providesTags: [ApiTag.DEVICE_DRIVER_CARDS],
        }),
        plSentRegister: build.mutation<void, {leoId: string}>({
            query: (request) => ({
                url: `/assets/${request.leoId}/pl-sent/register`,
                method: "POST",
                credentials: "include",
            }),
            invalidatesTags: [ApiTag.DEVICE, ApiTag.EETS_DEVICE, ApiTag.ASSET],
        }),
        roadsoftConfig: build.query<RoadsoftConfig, number>({
            query: (organizationId) => {
                return {
                    url: `/organizations/${organizationId}/roadsoftConfig`,
                    method: "GET",
                    credentials: "include",
                };
            },
            transformResponse: (response: {payload: RoadsoftConfig}) => response.payload,
            providesTags: [ApiTag.ROADSOFT],
        }),
        roadsoftConfigUpdate: build.mutation<void, RoadsoftConfigRequest>({
            query: (request) => ({
                url: `/organizations/${request.organizationId}/roadsoftConfig`,
                method: "PUT",
                credentials: "include",
                body: request,
            }),
            invalidatesTags: [ApiTag.ROADSOFT, ApiTag.ORGANIZATION],
        }),
        companyCardListAll: build.query<ListResponse<CompanyCardDetail>, ListRequest>({
            query: (request) => {
                const query = getQueryFromParams(request.params);
                return {
                    url: `/companyCards?${query.toString()}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: [ApiTag.COMPANY_CARD],
        }),
    }),
});

export const {
    useLoginMutation,
    useLogoutMutation,
    useSessionQuery,
    usePasswordUpdateMutation,
    useDeviceListQuery,
    useDeviceGetQuery,
    useDeviceHistoryListQuery,
    useDevicesCommandMutation,
    useEetsDeviceHistoryListQuery,
    useDeviceFirmwareUpdateMutation,
    useDeviceRestartFirmwareUpdateMutation,
    useDeviceCreateMutation,
    useDeviceRdlAuthenticateMutation,
    useDeviceRdlAuthenticationStatusQuery,
    useDeviceMassStorageDownloadMutation,
    useDeviceDriverCardDownloadMutation,
    useHardwareTypeListQuery,
    useOrderListQuery,
    useOrderGetQuery,
    useOrderUpdateAdminUserMutation,
    useOrderCancelMutation,
    useOrderStatusGetQuery,
    useOrderUpdateRecipientMutation,
    useOrderUpdateCommentMutation,
    useTerminationListQuery,
    useTerminationGetQuery,
    useTerminationUpdateMutation,
    useTerminationItemListQuery,
    useTerminationItemUpdateMutation,
    useTerminationCompleteMutation,
    useTerminationCommunicationListQuery,
    useShipmentListQuery,
    useShipmentStatusGetQuery,
    useShipmentCancelMutation,
    useShipmentGetQuery,
    useDefaultFirmwareListQuery,
    useDefaultFirmwareSetMutation,
    useFirmwareStateListQuery,
    useFirmwareTargetListQuery,
    useFirmwareListQuery,
    useFirmwareGetQuery,
    useFirmwareCreateMutation,
    useFirmwareUpdateMutation,
    useFirmwareUploadMutation,
    useFirmwareDeleteMutation,
    useStageListQuery,
    useUserListQuery,
    useUserGetQuery,
    useUserUpdateMutation,
    useUserCreateMutation,
    useUserDeleteMutation,
    useOrganizationListQuery,
    useOrganizationGetQuery,
    useOrganizationUpdateMutation,
    useOrganizationCreateMutation,
    useOrganizationCreateFromOrderMutation,
    useOrganizationStageListQuery,
    useCompanyCardByIdGetQuery,
    useCompanyCardCreateMutation,
    useCompanyCardDeleteMutation,
    useCompanyCardUpdateMutation,
    useOrganizationAdminUserListQuery,
    useOrganizationDKVCustomerNumberListQuery,
    useOrganizationDKVCustomerNumberCreateMutation,
    useOrganizationEetsDeviceListQuery,
    useOrganizationDeviceJobListQuery,
    useOrganizationAddStageMutation,
    useOrganizationDeviceAssignMutation,
    useOrganizationDeviceRemoveMutation,
    useEetsDeviceByIdGetQuery,
    useOrderShipmentCreateMutation,
    useDkvCustomerNameUpdateMutation,
    useDkvCustomerNameMismatchUpdateMutation,
    useCompanyCardServerQuery,
    useAssetListQuery,
    useDeviceDriverCardsQuery,
    usePlSentRegisterMutation,
    useRoadsoftConfigQuery,
    useRoadsoftConfigUpdateMutation,
    useCompanyCardListAllQuery,
} = api;
export {ItemType} from "./model/order";
export {ItemBranding} from "./model/order";
export {ItemConnectionType} from "./model/order";
